import { App, Form, Select, Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { REFUELING_ICON, UNIT_ICON, VEHICLE_UNIT_ICON } from "src/assets/svgs";
import { CustomButton, CustomSelect, CustomTypography } from "sipli-fleet-core/components";
import UnitInfoGreyCard from "../components/UnitInfoGreyCard";
import UnitManagementPanel from "../components/UnitManagementPanel";
import {
  extractLookupItems,
  extractVehicleSearchItems,
  getDeviceSerialFromVehicle,
  getFuelSensorSerialLabel,
  getQueryData,
  MIN_SEARCH_LENGTH,
  toOptions,
  toVehicleOptions,
} from "../shared/unitManagementUtils";
import {
  useMutationUninstallFuelSensor,
  useMutationUninstallUnit,
  useQueryGetUnitManagementAdjusterLookup,
  useQueryGetUnitManagementClientLookup,
  useQueryGetVehicleById,
  useQuerySearchVehicle,
} from "src/queries/unitManagement.queries";
import type { FuelSensorAssociation } from "src/services/unitManagementService";
import { useSelectDistributor } from "src/store/selectDistributorStore";
import { UninstallConfirmationModal } from "../components/DeviceManagementModals";
import "../Install/Install.css";
import "./Uninstall.css";

interface UninstallFormValues {
  adjusterId?: string | number;
  customerId?: string | number;
  vehicleId?: string | number;
}

const VehicleUnitIcon = () => (
  <img src={VEHICLE_UNIT_ICON} alt="" className="h-5 w-5" />
);

const UnitIcon = () => <img src={UNIT_ICON} alt="" className="h-5 w-5" />;

const FuelSensorIcon = () => <img src={REFUELING_ICON} alt="" className="h-5 w-5" />;

const Uninstall = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [form] = Form.useForm<UninstallFormValues>();
  const selectedCustomerId = Form.useWatch("customerId", form);
  const selectedVehicleId = Form.useWatch("vehicleId", form);
  const { selectedDistributor } = useSelectDistributor();
  const [vehicleSearch, setVehicleSearch] = useState("");
  const [uninstallUnitConfirmOpen, setUninstallUnitConfirmOpen] = useState(false);
  const [fuelSensorToUninstall, setFuelSensorToUninstall] =
    useState<FuelSensorAssociation | null>(null);
  const distributorId = selectedDistributor ?? undefined;

  const { data: adjusterData, isLoading: isAdjusterLoading } =
    useQueryGetUnitManagementAdjusterLookup(distributorId ? { distributorId } : {}, {
      enabled: !!distributorId,
    });
  const { data: clientData, isLoading: isClientLoading } = useQueryGetUnitManagementClientLookup(
    distributorId ? { distributorId } : {},
    { enabled: !!distributorId }
  );
  const { data: vehicleSearchData, isLoading: isVehicleSearchLoading } = useQuerySearchVehicle(
    selectedCustomerId
      ? {
          clientId: selectedCustomerId,
          query: vehicleSearch,
        }
      : undefined,
    { enabled: !!selectedCustomerId }
  );
  const {
    data: vehicleDetail,
    isLoading: isVehicleDetailLoading,
    refetch: refetchVehicle,
  } = useQueryGetVehicleById(selectedVehicleId, { enabled: !!selectedVehicleId });

  const { mutateAsync: uninstallUnit, isPending: isUninstallingUnit } = useMutationUninstallUnit();
  const { mutateAsync: uninstallFuelSensor, isPending: isUninstallingFuelSensor } =
    useMutationUninstallFuelSensor();

  const adjusterOptions = useMemo(
    () => toOptions(extractLookupItems(getQueryData(adjusterData))),
    [adjusterData]
  );

  const clientOptions = useMemo(
    () => toOptions(extractLookupItems(getQueryData(clientData))),
    [clientData]
  );

  const vehicleItems = useMemo(
    () => extractVehicleSearchItems(getQueryData(vehicleSearchData)),
    [vehicleSearchData]
  );

  const vehicleOptions = useMemo(() => toVehicleOptions(vehicleItems, t), [vehicleItems, t]);

  const deviceSerial = getDeviceSerialFromVehicle(vehicleDetail);
  const fuelSensors = vehicleDetail?.fuelSensors ?? [];

  const handleCustomerChange = () => {
    form.setFieldValue("vehicleId", undefined);
    setVehicleSearch("");
  };

  const handleReset = () => {
    form.resetFields();
    setVehicleSearch("");
    setUninstallUnitConfirmOpen(false);
    setFuelSensorToUninstall(null);
  };

  const handleOpenUninstallUnitConfirm = async () => {
    try {
      await form.validateFields();
      if (!vehicleDetail?.id) return;
      setUninstallUnitConfirmOpen(true);
    } catch {
      // validation handled by form
    }
  };

  const handleUninstallUnit = async () => {
    try {
      const values = await form.validateFields();
      if (!vehicleDetail?.id) return;

      await uninstallUnit({
        adjusterId: values.adjusterId as string | number,
        clientId: values.customerId as string | number,
        description: "",
        vehicleId: vehicleDetail.id,
      });

      message.success(t("unit_uninstalled_successfully"));
      handleReset();
    } catch {
      // validation or API errors handled by form / ApiService
    }
  };

  const handleUninstallFuelSensor = async (row: FuelSensorAssociation) => {
    if (!row.id) return;

    try {
      await uninstallFuelSensor(row.id);
      message.success(t("fuel_sensor_uninstalled_successfully"));
      setFuelSensorToUninstall(null);
      await refetchVehicle();
    } catch {
      // ApiService toast
    }
  };

  const fuelSensorColumns: ColumnsType<FuelSensorAssociation> = [
    {
      title: t("fuel_sensors"),
      key: "fuelSensor",
      render: (_, row) => getFuelSensorSerialLabel(row),
    },
    {
      title: t("quantity"),
      key: "quantity",
      render: (_, row) => row.unit?.quantity ?? "—",
    },
    {
      title: t("action"),
      key: "action",
      width: 120,
      render: (_, row) => (
        <CustomButton
          type="primary"
          buttonSize="small"
          label={t("uninstall_unit")}
          loading={isUninstallingFuelSensor}
          disabled={!row.id}
          className="!text-white"
          onClick={() => setFuelSensorToUninstall(row)}
        />
      ),
    },
  ];

  return (
    <div className="device-uninstall-card">
      <CustomTypography.CustomTitle className="!mb-6 !text-[19.93px] !font-bold !text-black">
        {t("uninstall_unit")}
      </CustomTypography.CustomTitle>

      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        className="device-uninstall-form"
        onValuesChange={(changed) => {
          if ("customerId" in changed) handleCustomerChange();
        }}
      >
        <UnitManagementPanel
          icon={<VehicleUnitIcon />}
          title={t("vehicle")}
          description={t("uninstall_vehicle_description")}
        >
          <div className="grid gap-x-2 md:grid-cols-3">
            <Form.Item
              name="adjusterId"
              label={`${t("adjuster")}*`}
              rules={[{ required: true, message: t("field_required") }]}
            >
              <CustomSelect
                placeholder={t("select")}
                options={adjusterOptions}
                loading={isAdjusterLoading}
                disabled={!distributorId}
                className="!h-8"
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>
            <Form.Item
              name="customerId"
              label={`${t("customer")}*`}
              rules={[{ required: true, message: t("field_required") }]}
            >
              <CustomSelect
                placeholder={t("select")}
                options={clientOptions}
                loading={isClientLoading}
                disabled={!distributorId}
                className="!h-8"
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>
            <Form.Item
              name="vehicleId"
              label={`${t("search_vehicle")}*`}
              rules={[{ required: true, message: t("field_required") }]}
            >
              <Select
                showSearch
                filterOption={false}
                placeholder={t("search_vehicle")}
                options={vehicleOptions}
                loading={isVehicleSearchLoading}
                disabled={!selectedCustomerId}
                className="!h-8"
                onSearch={setVehicleSearch}
                notFoundContent={
                  vehicleSearch.trim().length < MIN_SEARCH_LENGTH
                    ? t("type_at_least_3_characters")
                    : t("no_vehicle_found")
                }
              />
            </Form.Item>
          </div>

          {selectedVehicleId ? (
            <UnitInfoGreyCard
              icon={<UnitIcon />}
              label={t("associated_unit")}
              value={isVehicleDetailLoading ? "..." : deviceSerial || t("no_data")}
              action={
                <CustomButton
                  type="primary"
                  buttonSize="big"
                  label={t("uninstall_unit")}
                  loading={isUninstallingUnit || isVehicleDetailLoading}
                  disabled={!vehicleDetail?.id}
                  className="!text-white"
                  onClick={handleOpenUninstallUnitConfirm}
                />
              }
            />
          ) : null}
        </UnitManagementPanel>

        {fuelSensors.length > 0 ? (
          <div className="device-uninstall-fuel-section mt-4">
            <UnitManagementPanel
              icon={<FuelSensorIcon />}
              title={t("fuel_sensors")}
              description={t("unit_details_install_description")}
            >
              <Table<FuelSensorAssociation>
                columns={fuelSensorColumns}
                dataSource={fuelSensors}
                rowKey={(row) => String(row.id ?? getFuelSensorSerialLabel(row))}
                pagination={false}
                size="small"
              />
            </UnitManagementPanel>
          </div>
        ) : null}

        <div className="mt-8 flex justify-end gap-2">
          <CustomButton
            label={t("reset")}
            buttonSize="big"
            onClick={handleReset}
            disabled={isUninstallingUnit}
          />
        </div>
      </Form>

      <UninstallConfirmationModal
        open={uninstallUnitConfirmOpen}
        title={t("uninstall_unit_confirm_title")}
        description={t("uninstall_unit_confirm_description")}
        loading={isUninstallingUnit}
        onCancel={() => setUninstallUnitConfirmOpen(false)}
        onConfirm={handleUninstallUnit}
      />

      <UninstallConfirmationModal
        open={!!fuelSensorToUninstall}
        title={t("uninstall_fuel_sensor_confirm_title")}
        description={t("uninstall_fuel_sensor_confirm_description")}
        loading={isUninstallingFuelSensor}
        onCancel={() => setFuelSensorToUninstall(null)}
        onConfirm={() => {
          if (fuelSensorToUninstall) {
            void handleUninstallFuelSensor(fuelSensorToUninstall);
          }
        }}
      />
    </div>
  );
};

export default Uninstall;
